import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Clock3, AlertTriangle, AlertOctagon, Flame } from "lucide-react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const STYLES = {
  "0-15":  { color: "#2AA36F", bg: "bg-emerald-50",  border: "border-emerald-200", text: "text-emerald-700", Icon: Clock3 },
  "15-30": { color: "#E8A93A", bg: "bg-amber-50",    border: "border-amber-200",   text: "text-amber-700",   Icon: AlertTriangle },
  "30-60": { color: "#EA580C", bg: "bg-orange-50",   border: "border-orange-200",  text: "text-orange-700",  Icon: AlertOctagon },
  "60+":   { color: "#DC2626", bg: "bg-red-50",      border: "border-red-200",     text: "text-red-700",     Icon: Flame },
};

export default function AgingBucketsWidget({ onBucketClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/dashboard/aging-buckets")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (!data && !loading) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="aging-buckets-widget">
      <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="font-display font-bold text-slate-900 tracking-tight text-sm">
          Collection Health · Aging Buckets
        </div>
        {data && (
          <div className="text-[11px] text-slate-500">
            Total Outstanding:{" "}
            <span className="font-mono-num font-bold text-slate-800">{inr(data.total.amount)}</span>{" "}
            <span className="text-slate-400">({data.total.count} accounts)</span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4">
        {(data?.buckets || []).map((b) => {
          const s = STYLES[b.key] || STYLES["0-15"];
          const Icon = s.Icon;
          const clickable = b.count > 0 && b.min_days >= 15 && typeof onBucketClick === "function";
          return (
            <button
              key={b.key}
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onBucketClick(b.min_days)}
              className={`text-left rounded-md border ${s.border} ${s.bg} p-3 transition ${clickable ? "hover:shadow-md hover:-translate-y-0.5 cursor-pointer" : "cursor-default"}`}
              data-testid={`aging-bucket-${b.key}`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <div className={`text-[10px] uppercase tracking-widest font-bold ${s.text}`}>{b.label}</div>
                <Icon className="h-4 w-4" style={{ color: s.color }} strokeWidth={2} />
              </div>
              <div className="font-mono-num font-black text-2xl text-slate-900 leading-none">{inr(b.amount)}</div>
              <div className="text-[11px] text-slate-500 mt-1 font-mono-num">
                {b.count} account{b.count === 1 ? "" : "s"}
                {clickable && <span className="ml-1 text-[color:var(--brand-teal)] font-semibold">· view</span>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
